import { create } from 'zustand';
import { Business } from '@/types/homepage';
import { homepageApi } from '@/api/homepageApi';

interface MapBounds {
  northEast: { lat: number; lng: number };
  southWest: { lat: number; lng: number };
}

interface LoadBusinessesParams {
  bounds: MapBounds;
  isFromSearch?: boolean;
}

interface HomePageStore {
  businesses: Business[];
  mapState: 'idle' | 'loading' | 'error';
  lastSearchBounds: MapBounds | null;
  loadBusinessesForBounds: (params: LoadBusinessesParams) => Promise<void>;
}

// Utility to check if a point is within bounds with a buffer
const isPointInBounds = (
  point: [number, number], 
  bounds: MapBounds,
  buffer: number = 0.1 // 10% buffer to prevent edge cases
): boolean => {
  const [lng, lat] = point;
  const latBuffer = Math.abs(bounds.northEast.lat - bounds.southWest.lat) * buffer;
  const lngBuffer = Math.abs(bounds.northEast.lng - bounds.southWest.lng) * buffer;
  
  return lat >= bounds.southWest.lat - latBuffer &&
         lat <= bounds.northEast.lat + latBuffer &&
         lng >= bounds.southWest.lng - lngBuffer &&
         lng <= bounds.northEast.lng + lngBuffer;
};

// Check if all current businesses are still within the new bounds
const areAllBusinessesInBounds = (
  businesses: Business[],
  bounds: MapBounds
): boolean => {
  return businesses.every(business => {
    const coords = business.location?.coordinates?.coordinates;
    if (!coords || !Array.isArray(coords) || coords.length !== 2) return false;
    return isPointInBounds(coords as [number, number], bounds);
  });
};

// Check if bounds overlap significantly
const doBoundsOverlap = (bounds1: MapBounds | null, bounds2: MapBounds): boolean => {
  if (!bounds1) return false;

  const overlap = {
    north: Math.min(bounds1.northEast.lat, bounds2.northEast.lat),
    south: Math.max(bounds1.southWest.lat, bounds2.southWest.lat),
    east: Math.min(bounds1.northEast.lng, bounds2.northEast.lng),
    west: Math.max(bounds1.southWest.lng, bounds2.southWest.lng)
  };

  // Calculate overlap percentage
  const bounds1Area = (bounds1.northEast.lat - bounds1.southWest.lat) * 
                     (bounds1.northEast.lng - bounds1.southWest.lng);
  const bounds2Area = (bounds2.northEast.lat - bounds2.southWest.lat) * 
                     (bounds2.northEast.lng - bounds2.southWest.lng);
  const overlapArea = Math.max(0, overlap.north - overlap.south) * 
                     Math.max(0, overlap.east - overlap.west);

  const smallerArea = Math.min(bounds1Area, bounds2Area);
  return overlapArea / smallerArea > 0.7; // 70% overlap threshold
};

export const useHomePageStore = create<HomePageStore>((set, get) => ({
  businesses: [],
  mapState: 'idle',
  lastSearchBounds: null,

  loadBusinessesForBounds: async (params: LoadBusinessesParams) => {
    const { bounds, isFromSearch = false } = params;
    const currentState = get().mapState;
    const currentBusinesses = get().businesses;
    const lastBounds = get().lastSearchBounds;
    
    console.debug('[HomePageStore][DEBUG] loadBusinessesForBounds called:', {
      bounds,
      isFromSearch,
      currentState,
      currentBusinessCount: currentBusinesses.length,
      hasLastBounds: !!lastBounds
    });

    // Skip if already loading
    if (currentState === 'loading') {
      console.debug('[HomePageStore][DEBUG] Skipping load - already loading');
      return;
    }

    // If not a search operation, check if we really need to load new data
    if (!isFromSearch && currentBusinesses.length > 0 && lastBounds) {
      // Check if current bounds overlap significantly with last bounds
      const hasSignificantOverlap = doBoundsOverlap(lastBounds, bounds);
      
      // Check if all current businesses are still within the new bounds
      const allBusinessesInBounds = areAllBusinessesInBounds(currentBusinesses, bounds);

      console.debug('[HomePageStore][DEBUG] Bounds analysis:', {
        hasSignificantOverlap,
        allBusinessesInBounds,
        currentBusinessCount: currentBusinesses.length
      });

      // If we have significant overlap and all businesses are still in bounds, skip the update
      if (hasSignificantOverlap && allBusinessesInBounds) {
        console.debug('[HomePageStore][DEBUG] Skipping update - current businesses still valid');
        return;
      }
    }

    set({ mapState: 'loading' });

    try {
      console.debug('[HomePageStore][DEBUG] Making API call to fetch businesses');
      const response = await homepageApi.getBusinessesInBounds(bounds);

      if (!response.success) {
        throw new Error('Failed to fetch businesses');
      }

      const newBusinesses = response.businesses || [];
      
      // Compare business sets
      const currentIds = new Set(currentBusinesses.map(b => b._id));
      const newIds = new Set(newBusinesses.map(b => b._id));
      const addedBusinesses = newBusinesses.filter(b => !currentIds.has(b._id));
      const removedBusinesses = currentBusinesses.filter(b => !newIds.has(b._id));

      console.debug('[HomePageStore][DEBUG] Business comparison:', {
        currentCount: currentBusinesses.length,
        newCount: newBusinesses.length,
        added: addedBusinesses.length,
        removed: removedBusinesses.length
      });

      // Only update if the business set has actually changed
      if (addedBusinesses.length > 0 || removedBusinesses.length > 0) {
        console.debug('[HomePageStore][DEBUG] Updating state with new businesses:', {
          addedCount: addedBusinesses.length,
          removedCount: removedBusinesses.length
        });
        
        set({
          businesses: newBusinesses,
          mapState: 'idle',
          lastSearchBounds: bounds
        });
      } else {
        console.debug('[HomePageStore][DEBUG] Skipping update - businesses unchanged');
        set({ 
          mapState: 'idle',
          lastSearchBounds: bounds
        });
      }

    } catch (error) {
      console.error('[HomePageStore][ERROR] Failed to load businesses:', error);
      set({ mapState: 'error' });
    }
  }
})); 